import { MouseEvent, useEffect, useRef, useState } from "react"
import { Button, Card, Stack, Typography } from "@mui/material"
import Grid from '@mui/material/Grid2';
import { CardInfo } from "./style"
import { IconParticipationProjects } from "../../../icons/IconParticipationProjects"
import { GroupRoundedIcon } from "../../../icons/GroupRoundedIcon"
// import { IconParticipationSocialOrders } from "../../../icons/IconParticipationSocialOrders"
import { useNavigate } from "react-router-dom"
import type { TActiveUserResponse } from "../../../api/types"
import { routesProfile } from "../../../consts"
import modalEmitter from "../../ModalComponent/modalEmitter"
import {
  InviteProjectModalBody,
  InviteTeamModalBody,
  UserFollowersModalBody,
} from "../../Modals"
import { formatNumber } from "../../../common/FormatNumber"
import { profileApi } from "../../../api/profile";
import { useFollowUserMutation, useUnfollowUserMutation } from "../../../api/users";
import { useSettledToggleNetwork } from "../../../hooks/useSettledToggleNetwork"

interface UserInfoProps {
  user: TActiveUserResponse
}

export const UserInfo = ({ user }: UserInfoProps) => {
  const navigate = useNavigate()
  const { data: me } = profileApi.useGetProfileQuery()
  const [followUser, { isLoading: isFollowLoading }] = useFollowUserMutation()
  const [unfollowUser, { isLoading: isUnfollowLoading }] = useUnfollowUserMutation()
  const [isFollowed, setIsFollowed] = useState(user.is_followed === true)
  const followSettle = useSettledToggleNetwork()
  const followBurstRef = useRef<boolean | null>(null)

  useEffect(() => {
    setIsFollowed(user.is_followed === true)
  }, [user.id, user.is_followed])

  const handleNavigateToProjects = () => navigate(`/${routesProfile.USERS.path}/${user.id}/projects`)
  // const handleNavigateToOrders = () => navigate(`/${routesProfile.USERS.path}/${user.id}/socialorders`)

  const handleEdit = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    navigate(`/${routesProfile.PROFILE.path}`)
  };

  const isFollowActionLoading = isFollowLoading || isUnfollowLoading
  const followActionLabel = isFollowActionLoading
    ? 'Загрузка...'
    : (isFollowed ? 'Отписаться' : 'Подписаться')

  const handleFollow = () => {
    if (isFollowActionLoading) {
      return
    }

    if (followBurstRef.current === null) {
      followBurstRef.current = isFollowed
    }
    const next = !isFollowed
    setIsFollowed(next)

    followSettle.schedule({
      desired: next,
      commit: async (desired) => {
        followBurstRef.current = null
        if (desired) {
          await followUser({ userId: user.id }).unwrap()
        } else {
          await unfollowUser({ userId: user.id }).unwrap()
        }
      },
      onSkip: () => {
        followBurstRef.current = null
      },
      onError: (baseline) => {
        followBurstRef.current = null
        setIsFollowed(baseline)
      },
    })
  }

  const isOwnProfile = me?.id === user.id
  const subscribersCount = Number(user.counters?.subscribers ?? 0)

  const handleOpenFollowers = () => {
    if (subscribersCount <= 0) {
      return
    }

    modalEmitter.emitOpenModal({
      dialog: {
        title: 'Ваши подписчики',
        body: <UserFollowersModalBody />,
        modalToCentrPosition: true,
      },
    })
  }

  const handleInviteProject = () => {
    modalEmitter.emitOpenModal({
      dialog: {
        title: "Пригласить в проект",
        body: <InviteProjectModalBody userId={user.id} />,
      },
      closeAtion: { visible: false },
      confirmAtion: { visible: false },
    })
  }

  const handleInviteTeam = () => {
    modalEmitter.emitOpenModal({
      dialog: {
        title: "Пригласить в команду",
        body: <InviteTeamModalBody userId={user.id} />,
      },
      closeAtion: { visible: false },
      confirmAtion: { visible: false },
    })
  }

  return (
    <Card variant="primaryAdaptiveCard" sx={CardInfo}>
      <Stack direction="column" gap={{ xs: 1.5, sm: 2 }}>
        <Typography variant="h3">Статистика пользователя</Typography>
        {me?.id === user.id ? (
          <Button variant="follow" onClick={handleEdit}>Редактировать</Button>
        ) : (
          <Stack direction="column" gap={1}>
            <Button variant="follow" onClick={handleFollow} disabled={isFollowActionLoading}>
              {followActionLabel}
            </Button>
            <Button variant="action" onClick={handleInviteProject}>
              Пригласить в проект
            </Button>
            <Button variant="action" onClick={handleInviteTeam}>
              Пригласить в команду
            </Button>
          </Stack>
        )}
        <Grid container spacing={1}>
          {(user.counters?.projects ?? 0) > 0 && (
            <Grid size={{ xs: 12, sm: 6, md: 12 }} >
              <Stack
                direction="row"
                gap={1}
                alignItems="center"
                onClick={handleNavigateToProjects}
                sx={{ cursor: 'pointer' }}
              >
                <IconParticipationProjects />
                  <Typography>Основал {user.counters.projects} проекта</Typography>
              </Stack>
            </Grid>
          )}
          {isOwnProfile && (
            <Grid size={{ xs: 12, sm: 6, md: 12 }}>
              <Stack
                direction="row"
                gap={1}
                alignItems="center"
                role={subscribersCount > 0 ? 'button' : undefined}
                tabIndex={subscribersCount > 0 ? 0 : undefined}
                aria-label={
                  subscribersCount > 0
                    ? 'Открыть список подписчиков'
                    : undefined
                }
                onClick={subscribersCount > 0 ? handleOpenFollowers : undefined}
                onKeyDown={(e) => {
                  if (subscribersCount <= 0) {
                    return
                  }
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleOpenFollowers()
                  }
                }}
                sx={{
                  cursor: subscribersCount > 0 ? 'pointer' : 'default',
                  ...(subscribersCount > 0 && {
                    '&:hover .UserInfo-subscribersLabel': {
                      color: 'primary.main',
                      textDecoration: 'underline',
                    },
                  }),
                }}
              >
                <GroupRoundedIcon sx={{ width: 28, height: 28 }} />
                <Typography className="UserInfo-subscribersLabel" sx={{ transition: 'color 0.15s ease' }}>
                  Подписчиков: {formatNumber(subscribersCount)}
                </Typography>
              </Stack>
            </Grid>
          )}
          {/* TODO: Добавить проверку на соц-заказы как они появятся */}
          {/* <Grid size={{ xs: 12, sm: 6, md: 12 }}>
            <Stack
              direction="row"
              gap={1}
              alignItems="center"
              onClick={handleNavigateToOrders}
              sx={{ cursor: 'pointer' }}
            >
              <IconParticipationSocialOrders />
              <Typography>Создал 10 соц-заказов</Typography>
            </Stack>
          </Grid> */}
        </Grid>
      </Stack>
    </Card>
  )
}
